import { FlowcodeLimitUpsellModalProps } from './FlowcodeLimitUpsellModalProps'
import { LimitUpsellModal } from '../LimitUpsellModal/LimitUpsellModal'
import { LockModalGraphic } from '@app/common/src/constants/lockModal'
import { LockModalVariants } from '@dtx-company/inter-app/src/constants/lockModal'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PromoAsset } from '@app/common/src/components/LockModal/PromoAsset/PromoAsset'
import { useBillingPlanType } from '@app/common/src/hooks/useBillingPlanType/useBillingPlanType'
import React, { forwardRef } from 'react'

// these graphics are carried over from the giant lock modals data structure
// as an interim solution to migrating to a more flexible upsell experience.
// In the future these will likely be removed in favor of an entirely better AIDA
// experience before the user reaches the upgrade section.

const PRO_GRAPHIC: LockModalGraphic = {
  header: '',
  customHeadingText: `You have hit your Flowcode limit!`,
  src: '/graphics/lockModal/fc-pro-code-limit3-2x.png',
  height: 663,
  width: 305,
  bg: '#242424',
  hideCompanies: true
}

const PRO_PLUS_AND_HIGHER_GRAPHIC: LockModalGraphic = {
  header: 'Reach, engage & convert customers with more Flowcodes',
  src: '/graphics/lockModal/fc-pro-code-limit.svg',
  bg: '/graphics/lockModal/pro-graphic-background.svg'
}

/**
 * Use this upsell experience whenever a user hits their Flowcode limit.
 *
 * It will determine how to upsell them based on their current plan limit and what plans
 * are available to provide them with a higher limit.
 *
 * Generally this modal should be opened when the user attempts an action, such as creating
 * a single Flowcode or batch of Flowcodes, which would put them over their limit.
 *
 * The modal may be dismissed, returning the user to the screen they were previously at.
 * If the user is successfully upsold, the action they initially took should be automatically
 * re-executed in order to provide a seamless experience.
 *
 * @see UpsellModal
 * @see LimitUpsellModal
 *
 * @example
 * function createFlowcode() {
 *   try {
 *     // do things
 *   } catch (error) {
 *     if (error instanceof FlowcodeLimitError) {
 *       setLimitModalOpen(true)
 *     } else {
 *       // handle things
 *     }
 *   }
 * }
 * <FlowcodeLimitUpsellModal
 *   open={limitModalOpen}
 *   onClose={(reason) => {
 *     setLimitModalOpen(false)
 *     if (reason === 'success') {
 *       createFlowcode()
 *     }
 *   }}
 * />
 */
export const FlowcodeLimitUpsellModal = forwardRef<HTMLDivElement, FlowcodeLimitUpsellModalProps>(
  function FlowcodeLimitUpsellModal(props, ref): JSX.Element {
    // TODO: build a hook that determines the next plan type based on /available-plans
    const billingPlanType = useBillingPlanType()
    const nextPlanType = (() => {
      switch (billingPlanType) {
        case PlanType.PRO_PLUS: // fallthrough
        case PlanType.PRO_PLUS_ORG:
          return PlanType.PRO_FLEX
        case PlanType.PRO:
          return PlanType.PRO_PLUS_ORG
      }
      return PlanType.PRO
    })()

    const heapLockSelected = (() => {
      // TODO: add growth variant
      switch (nextPlanType) {
        case PlanType.PRO_FLEX:
          return 'fcProFlexCodeLimit'
        case PlanType.PRO_PLUS_ORG:
          return LockModalVariants.FC_PRO_PLUS_CODE_LIMIT.toString()
      }
      return LockModalVariants.FC_PRO_CODE_LIMIT.toString()
    })()

    const graphic: LockModalGraphic = (() => {
      switch (nextPlanType) {
        case PlanType.PRO:
          return PRO_GRAPHIC
      }
      return PRO_PLUS_AND_HIGHER_GRAPHIC
    })()

    return (
      <LimitUpsellModal
        planType={nextPlanType}
        customHeadingText={graphic.customHeadingText}
        promoAsset={<PromoAsset graphic={graphic} />}
        promoBackgroundImageUrl={graphic.bg}
        promoHeading={graphic.header}
        promoHideCompanies={graphic.hideCompanies}
        heapLockSelected={heapLockSelected}
        {...props}
        ref={ref}
      />
    )
  }
)
